import { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Switch } from '../../../components/Switch';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import apiClient from '../../../lib/api';
import toast from 'react-hot-toast';

const TIMEZONES = ['UTC', 'America/Los_Angeles', 'America/New_York', 'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney'];
const DAYS: { key: string; label: string }[] = [
    { key: 'mon', label: 'M' }, { key: 'tue', label: 'T' }, { key: 'wed', label: 'W' },
    { key: 'thu', label: 'T' }, { key: 'fri', label: 'F' }, { key: 'sat', label: 'S' }, { key: 'sun', label: 'S' },
];

interface WorkSettingsData {
    workspaceName: string;
    timezone: string;
    workingDays: string[];
    autoAssignCreator: boolean;
    requireEstimations: boolean;
    strictSprints: boolean;
}

const DEFAULTS: WorkSettingsData = {
    workspaceName: '', timezone: 'UTC', workingDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
    autoAssignCreator: true, requireEstimations: false, strictSprints: false,
};

export function WorkSettings() {
    const [form, setForm] = useState<WorkSettingsData>(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiClient.get('/work/settings');
                const d = res.data || {};
                setForm({
                    workspaceName: d.workspaceName ?? '',
                    timezone: d.timezone ?? 'UTC',
                    workingDays: Array.isArray(d.workingDays) ? d.workingDays : DEFAULTS.workingDays,
                    autoAssignCreator: d.autoAssignCreator ?? true,
                    requireEstimations: d.requireEstimations ?? false,
                    strictSprints: d.strictSprints ?? false,
                });
            } catch {
                /* keep defaults */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const toggleDay = (key: string) => {
        setForm(f => ({
            ...f,
            workingDays: f.workingDays.includes(key) ? f.workingDays.filter(d => d !== key) : [...f.workingDays, key],
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiClient.put('/work/settings', form);
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Work Settings</h1>
                    <p className="text-muted-foreground">Configure your workspace rules and preferences.</p>
                </div>
                <Button variant="primary" onClick={handleSave} isLoading={saving} disabled={loading}>Save Changes</Button>
            </div>

            {loading ? (
                <div className="p-12 text-center text-muted-foreground">Loading settings…</div>
            ) : (
                <div className="max-w-3xl space-y-6">
                    <Card>
                        <CardHeader className="border-b border-border pb-4">
                            <h3 className="text-foreground font-display text-section-title">Workspace</h3>
                            <p className="text-sm text-muted-foreground">Core configuration for your team.</p>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-6">
                            <div className="space-y-2 max-w-md">
                                <label className="text-sm font-medium text-foreground">Workspace Name</label>
                                <Input value={form.workspaceName} onChange={(e) => setForm(f => ({ ...f, workspaceName: e.target.value }))} placeholder="e.g. Engineering" />
                            </div>
                            <div className="space-y-2 max-w-md">
                                <label className="text-sm font-medium text-foreground">Default Timezone</label>
                                <Select value={form.timezone} onValueChange={(v) => setForm(f => ({ ...f, timezone: v }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Working Days</label>
                                <div className="flex gap-2">
                                    {DAYS.map(d => {
                                        const active = form.workingDays.includes(d.key);
                                        return (
                                            <button key={d.key} type="button" onClick={() => toggleDay(d.key)}
                                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/70'}`}>
                                                {d.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">Used for capacity planning and burndown charts.</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="border-b border-border pb-4">
                            <h3 className="text-foreground font-display text-section-title">Issue Tracking Defaults</h3>
                            <p className="text-sm text-muted-foreground">Default behaviors for the task tracker.</p>
                        </CardHeader>
                        <CardContent className="space-y-5 pt-6">
                            {[
                                { key: 'autoAssignCreator' as const, title: 'Auto-assign Creator', desc: 'Assign the creator if no assignee is set.' },
                                { key: 'requireEstimations' as const, title: 'Require Estimations', desc: 'Force estimates before moving to In Progress.' },
                                { key: 'strictSprints' as const, title: 'Strict Sprints', desc: 'Prevent adding issues to active sprints without approval.' },
                            ].map(row => (
                                <div key={row.key} className="flex items-center justify-between border-b border-border pb-4 last:border-0 last:pb-0">
                                    <div>
                                        <h4 className="text-foreground text-sm font-semibold">{row.title}</h4>
                                        <p className="text-xs text-muted-foreground">{row.desc}</p>
                                    </div>
                                    <Switch checked={form[row.key]} onCheckedChange={(v) => setForm(f => ({ ...f, [row.key]: v }))} />
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
